import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Plus } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppDataTable, EmptyState, FilterToolbar, StatusBadge,
} from "../../../ui";
import apiClient from "../../../../utils/apiClient";
import { DASH, formatDate, formatTimeOfDay, orDash } from "../../../../utils/format";
import { ScorePill } from "../../../Portfolio/ResumeReport";
import RecordCardHead, { TableHeading } from "../RecordCardHead";
import RecordStat from "../RecordStat";
import { useResource } from "../useStudentRecord";
import { nextInterviewRound, summarizeInterviews } from "../studentRecord";

const MOCK_PAGE_SIZE = 10;
const PLACEMENT_WANTED = { Yes: "Wanted", No: "Not wanted" };

/** The round a drive is at — the last one on it, which is the newest. */
const latestRound = (iv) => (iv.rounds?.length ? iv.rounds[iv.rounds.length - 1] : null);

const INTERVIEW_COLUMNS = [
  {
    key: "company",
    label: "Company",
    render: (iv) => (
      <span className="tw-block tw-min-w-0">
        <span className="tw-block tw-font-medium tw-text-content">{iv.companyName}</span>
        {iv.hrName && <span className="tw-block tw-text-xs tw-text-content-subtle">HR: {iv.hrName}</span>}
      </span>
    ),
  },
  {
    key: "status",
    label: "Status",
    render: (iv) => <StatusBadge status={iv.currentStatus || "Scheduled"} domain="interview" />,
  },
  {
    key: "round",
    label: "Latest round",
    render: (iv) => {
      const r = latestRound(iv);
      if (!r) return <span className="tw-text-content-subtle">No rounds yet</span>;
      return (
        <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span>
            {r.round}
            <span className="tw-block tw-text-xs tw-tabular-nums tw-text-content-subtle">
              {formatDate(r.date)}{r.time ? `, ${formatTimeOfDay(r.time)}` : ""}{r.mode ? ` · ${r.mode}` : ""}
            </span>
          </span>
          <StatusBadge status={r.status} domain="interviewRound" />
        </span>
      );
    },
  },
  {
    key: "rounds",
    label: "Rounds",
    align: "right",
    render: (iv) => <span className="tw-tabular-nums">{iv.rounds?.length || 0}</span>,
  },
  {
    key: "updated",
    label: "Updated",
    render: (iv) => <span className="tw-tabular-nums tw-text-content-muted">{formatDate(iv.updatedAt || iv.createdAt)}</span>,
  },
];

const MOCK_COLUMNS = [
  { key: "date", label: "Date", render: (m) => <span className="tw-tabular-nums">{formatDate(m.createdAt)}</span> },
  {
    key: "topic",
    label: "Technology",
    render: (m) => (
      <span className="tw-block tw-min-w-0">
        <span className="tw-block tw-font-medium tw-text-content">
          {m.config?.technology || m.config?.topics?.join(", ") || DASH}
        </span>
        {(m.config?.role || m.config?.interviewType) && (
          <span className="tw-block tw-text-xs tw-text-content-subtle">
            {[m.config?.interviewType, m.config?.role].filter(Boolean).join(" · ")}
          </span>
        )}
      </span>
    ),
  },
  { key: "difficulty", label: "Level", render: (m) => orDash(m.config?.difficulty) },
  {
    key: "score",
    label: "Score",
    render: (m) => (m.status === "completed" && m.overallScore != null
      ? <ScorePill score={m.overallScore} size="sm" />
      : DASH),
  },
  { key: "status", label: "Status", render: (m) => <StatusBadge status={m.status} domain="mockInterview" /> },
];

/**
 * PlacementTab — is the student ready to be placed, and where they have
 * interviewed: the real drives, and the AI mock interviews that prepare for them.
 *
 * The resume and the rest of the portfolio are on the Portfolio tab; this tab
 * carries only the score, which is the number a placement officer compares.
 *
 * Props:
 *   student, stats, portfolio, interviews — the page's data
 *   features        — which plan features are on
 *   onAddInterview  — omitted when the viewer may not manage interviews
 */
const PlacementTab = ({ student, stats, portfolio, interviews, features, onAddInterview }) => {
  const navigate = useNavigate();
  const [mockPage, setMockPage] = useState(1);
  const mocks = useResource(
    `${student._id}:mocks:${mockPage}`,
    () => apiClient.get(`/mock-interview/student/${student._id}`, { params: { page: mockPage, limit: MOCK_PAGE_SIZE } })
      .then((r) => r.data),
    features.aiMock,
  );

  const analysis = portfolio.data?.latestAnalysis;
  const profile = portfolio.data?.portfolio;
  const summary = summarizeInterviews(interviews.data || []);
  const next = nextInterviewRound(interviews.data || []);
  const mock = stats.data?.mockInterview;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <RecordCardHead
          title="Placement readiness"
          description={next
            ? `Next round: ${next.round} with ${next.companyName} on ${formatDate(next.date)}${next.time ? `, ${formatTimeOfDay(next.time)}` : ""}.`
            : "No interview rounds scheduled."}
        />
        <AppCardContent>
          <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 md:tw-grid-cols-3 min-[1200px]:tw-grid-cols-6">
            <RecordStat label="Placement support" value={PLACEMENT_WANTED[student.placement] || DASH} />
            <RecordStat label="Target role" value={orDash(profile?.careerProfile?.targetRole)} />
            <RecordStat
              label="Resume score"
              value={analysis?.status === "completed" ? <ScorePill score={analysis.overallScore} size="sm" /> : DASH}
              hint={analysis?.status === "completed" ? `analysed ${formatDate(analysis.completedAt || analysis.createdAt)}` : profile?.resume?.key ? "Not analysed" : "No resume"}
            />
            {features.interviews && (
              <RecordStat label="Interviews" value={summary.total} hint={`${summary.inProgress} in progress`} />
            )}
            {features.interviews && (
              <RecordStat label="Offers" value={summary.offers} tone={summary.offers ? "success" : undefined} hint={`${summary.joined} joined`} />
            )}
            {features.aiMock && (
              <RecordStat
                label="Mock interviews"
                value={mock?.totalInterviews ? `${mock.avgScore}%` : DASH}
                hint={mock?.totalInterviews ? `avg of ${mock.totalInterviews} · best ${mock.highestScore}%` : "None taken"}
              />
            )}
          </dl>
        </AppCardContent>
      </AppCard>

      {features.interviews && (
        <AppDataTable
          fill={false}
          toolbar={(
            <FilterToolbar
              flush
              actions={onAddInterview && (
                <AppButton size="sm" variant="outline" onClick={onAddInterview}>
                  <Plus size={16} /> Add interview
                </AppButton>
              )}
            >
              <TableHeading title="Company interviews" count={summary.total ? String(summary.total) : ""} />
            </FilterToolbar>
          )}
          columns={INTERVIEW_COLUMNS}
          rows={interviews.data || []}
          rowKey={(iv) => iv._id}
          loading={interviews.loading && !interviews.data}
          onRowClick={(iv) => navigate(`/interviews/${iv._id}`)}
          emptyMessage={interviews.error ? "Could not load interviews." : "No company interviews recorded yet."}
        />
      )}

      {features.aiMock && (
        <AppDataTable
          fill={false}
          toolbar={(
            <FilterToolbar flush>
              <TableHeading
                title="AI mock interviews"
                count={mocks.data?.pagination?.total ? String(mocks.data.pagination.total) : ""}
              />
            </FilterToolbar>
          )}
          columns={MOCK_COLUMNS}
          rows={mocks.data?.data || []}
          rowKey={(m) => m._id}
          loading={mocks.loading}
          onRowClick={(m) => navigate(`/mock-interview/summary/${m._id}`)}
          emptyMessage={mocks.error ? "Could not load mock interviews." : "No mock interviews taken yet."}
          pagination={{
            page: mockPage,
            totalPages: mocks.data?.pagination?.pages || 1,
            total: mocks.data?.pagination?.total || 0,
            pageSize: MOCK_PAGE_SIZE,
            onChange: setMockPage,
            infoLabel: "mock interviews",
          }}
        />
      )}

      {!features.interviews && !features.aiMock && (
        <EmptyState
          icon={Briefcase}
          title="Interview tracking is not switched on"
          description="Company interviews and AI mock interviews are separate features. Once either is on, this student's are listed here."
        />
      )}
    </div>
  );
};

export default PlacementTab;
